import { useDispatch } from "react-redux";
import { useState, useEffect } from "react";
import { Dialog } from "@mui/material";
import { UserConfig } from "app/types";
import { AppDispatch } from "app/store";
import { actions } from "app/configSlice";
import kidsI18n from "common/i18n";
import { useScale, usePlaySound } from "common/utils";
import { ParentCloseBtn, FadeTransition } from "common/elements";
import i18n from "features/parent/i18n";

interface LanguageEditModalProps {
  open: boolean;
  currentLocale: string;
  activeUid: string | undefined;
  selectedUid: string;
  userConfig: UserConfig;
  updateUserConfig: (config: UserConfig) => void;
  onClose: () => void;
}

type LocaleOption = "ja" | "ja_kanji" | "en" | "zh_tw";

const normalizeLocale = (locale?: string): LocaleOption => {
  if (!locale || locale === "ja") return "ja";
  if (locale === "zh_cn") return "zh_tw";
  if (locale === "ja_kanji" || locale === "en" || locale === "zh_tw") {
    return locale;
  }
  return "ja";
};

export const LanguageEditModal = ({
  open,
  currentLocale,
  activeUid,
  selectedUid,
  userConfig,
  updateUserConfig,
  onClose,
}: LanguageEditModalProps) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const dispatch = useDispatch<AppDispatch>();
  const [locale, setLocale] = useState<LocaleOption>(normalizeLocale(currentLocale));

  useEffect(() => {
    if (open) {
      setLocale(normalizeLocale(currentLocale));
    }
  }, [open, currentLocale]);

  const handleClick = () => {
    play();
    const newConfig = { ...userConfig, locale };
    if (selectedUid === activeUid) {
      dispatch(actions.updateUserConfig({ key: "locale", value: locale }));
      kidsI18n.locale = locale;
    }
    updateUserConfig(newConfig);
    onClose();
  };

  const localeOptions: { value: LocaleOption; label: string }[] = [
    { value: "ja", label: i18n.t("MSG_PARENT_PAGE_HIRAGANA") },
    { value: "ja_kanji", label: i18n.t("MSG_PARENT_PAGE_KANJI") },
    { value: "en", label: i18n.t("MSG_PARENT_PAGE_LANGUAGE_EN") },
    { value: "zh_tw", label: i18n.t("MSG_PARENT_PAGE_LANGUAGE_ZH_TW") },
  ];

  return (
    <Dialog
      fullScreen
      open={open}
      maxWidth={false}
      sx={{ zIndex: 99999 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div className="flex-col-center w-full h-full bg-textcolor-black/50">
        <div style={{ transform: `scale(${scale})` }}>
          <div className="flex-col-el w-[510px] border-solid border-gray2-30 border-[16px] rounded-[16px] bg-gray2-30">
            <div className="flex-col-el justify-start w-full h-full relative p-6 rounded-[8px] bg-white">
              <ParentCloseBtn close={onClose} />
              <div className="flex-row-el items-center gap-2 mb-4 pb-3 border-b border-outline shrink-0">
                <span className="material-symbols-outlined !text-[54px] text-red-100">
                  language
                </span>
                <h2 className="text-[33px] text-textcolor-black">
                  {i18n.t("MSG_PARENT_PAGE_LANGUAGE_EDIT_TITLE")}
                </h2>
              </div>
              <div className="flex-col-view items-start py-4">
                <p className="text-body text-textcolor-black text-[24px]">
                  {i18n.t("MSG_PARENT_PAGE_LANGUAGE_EDIT_MESSAGE")}
                </p>

                <div className="flex-col-el w-full gap-3 my-6">
                  {localeOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        play();
                        setLocale(option.value);
                      }}
                      className={`flex-row-el items-center gap-3 w-full p-4 rounded-[12px] border-2 transition-all ${
                        locale === option.value
                          ? "bg-gray2-20 border-ink"
                          : "bg-white border-transparent hover:bg-surface"
                      }`}
                    >
                      <div
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          locale === option.value ? "border-ink" : "border-gray2-60"
                        }`}
                      >
                        {locale === option.value && <div className="w-3 h-3 rounded-full bg-ink" />}
                      </div>
                      <span className="text-[27px] text-textcolor-black">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex-row-el flex-center gap-4">
                <button
                  onClick={onClose}
                  className="flex-row-el items-center justify-center w-[160px] py-3 bg-white border border-gray2-60 text-textcolor-black rounded-[12px] text-[24px] cursor-pointer hover:bg-gray2-20 transition-all"
                >
                  {i18n.t("MSG_CANCEL_BTN_BY_PARENT")}
                </button>
                <button
                  onClick={handleClick}
                  className="flex-row-el items-center justify-center w-[160px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all"
                >
                  {i18n.t("MSG_CONFIRM_BTN_BY_PARENT")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
